import { IAIProvider } from '../providers/interfaces/IAIProvider';
import { RephraseOptions, RephraseResult } from '../types/aiTypes';
import { defaultOrchestratorConfig, AIOrchestratorConfig } from './types';
import { log, logError } from '../../utils/logging';
import { AppError } from '../../utils/errorHandling';

export class AIOrchestrator {
  private provider: IAIProvider;
  private config: AIOrchestratorConfig;

  constructor(provider: IAIProvider, config?: Partial<AIOrchestratorConfig>) {
    this.provider = provider;
    this.config = { ...defaultOrchestratorConfig, ...config };
  }

  /**
   * Rephrases text with retry logic and error handling
   * @param completeRephrase - If true, performs a complete rephrase (not just polishing)
   */
  async rephrase(text: string, options?: RephraseOptions, completeRephrase?: boolean): Promise<RephraseResult> {
    if (!text || text.trim().length === 0) {
      throw new AppError('Text cannot be empty', 'EMPTY_TEXT', 'Please select some text to rephrase.');
    }

    for (let attempt = 1; attempt <= this.config.maxRetries; attempt++) {
      try {
        log(`Rephrase attempt ${attempt}/${this.config.maxRetries}`);
        const result = await this.provider.rephrase(text, options, completeRephrase);
        
        // Validate result
        if (!result.rephrasedText || result.rephrasedText.trim().length === 0) {
          throw new AppError('Empty rephrased text received', 'EMPTY_RESULT', 'The AI returned an empty result. Please try again.');
        }

        log('Rephrase successful');
        return result;
      } catch (error) {
        logError(`Rephrase attempt ${attempt} failed`, error);

        // Don't retry on certain errors
        if (error instanceof AppError && error.code === 'EMPTY_TEXT') {
          throw error;
        }

        // Wait before retrying (except on last attempt)
        if (attempt < this.config.maxRetries) {
          await this.delay(this.config.retryDelay * attempt); // Exponential backoff
        }
      }
    }

    // All retries failed
    throw new AppError(
      'Failed to rephrase after multiple attempts',
      'REPHRASE_FAILED',
      'Unable to rephrase the text after several attempts. Please try again later.'
    );
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get the current AI provider instance
   */
  getCurrentProvider(): IAIProvider {
    return this.provider;
  }
}

